import fp from "lodash/fp"

import type { ReactNode } from "react"
import type { TFunction } from "i18next"

import type { DetailsGroup, Paged } from "types/common.types"
import type { Device } from "types/device.types"
import type { PowerlineSpansReq, PowerlineSpansRes, Span } from "types/spans.types"

import { customValueFormatter } from "helpers/formatters/plots/plots"
import { formatDateFromISO, formatDayFromISO } from "helpers/utils/dateUtils"
import { isVirtualDevice } from "helpers/utils/devices"
import { formatAmpacity, formatLength } from "helpers/utils/powerlines"

/** 1-based position in the powerline's `span_ids`, or null when unknown. */
export const getSpanIndex = (spanId: number, spanIds?: number[]): number | null => {
  const index = spanIds?.indexOf(spanId) ?? -1
  return index === -1 ? null : index + 1
}

/**
 * `Span.name` is nullable and most spans on a line have none, so the fallback is
 * the position in the line ("Span 42"), which is how operators refer to them.
 * Deliberately not named after the tower pair: several spans can sit between the
 * same two towers. Falls back to the ID when the position is unknown.
 */
export const getSpanDisplayName = (
  span: Pick<Span, "id" | "name">,
  spanIds: number[] | undefined,
  t: TFunction,
): string => {
  if (span.name) {
    return span.name
  }

  const index = getSpanIndex(span.id, spanIds)
  return index === null
    ? `${t("entities.SPAN")} ${span.id}`
    : t("spans.SPAN_INDEX", { index })
}

/** Nearly every span attribute is optional, so a missing one reads as a dash. */
export const formatSpanValue = (
  value: number | null | undefined,
  locale: string,
  format: (value: number, locale: string) => string,
): string => (fp.isNil(value) ? "-" : format(value, locale))

export const formatTemperature = (value: number, locale: string) =>
  customValueFormatter({
    suffix: "°C",
    decimalPlaces: 1,
    formatUnits: false,
    locale,
  })(value)

/** Tensions are stored in newtons and run to the tens of thousands. */
export const formatTension = (value: number, locale: string) =>
  customValueFormatter({
    suffix: "N",
    decimalPlaces: 2,
    locale,
  })(value)

/**
 * The clamp that holds the conductor to the tower's insulator. The model
 * accepts a free string next to the enum, so an unknown value falls back to
 * the stored one.
 */
export const getClampTypeLabel = (clampType: string, t: TFunction): string =>
  t(`spans.clampTypes.${clampType}`, {
    defaultValue: fp.capitalize(clampType.replaceAll("_", " ")),
  })

/**
 * A device that reports for a span: installed on the line, pointed at a span,
 * and a real instrument rather than one of the synthetic ones.
 */
const isSpanDevice = (device: Device): boolean =>
  !isVirtualDevice(device.device_type) &&
  device.settings?.installation_type === "powerline" &&
  !fp.isNil(device.settings?.span_id)

/** The devices reporting for one span. Nothing stops there being several. */
export const getSpanDevices = (
  devices: Device[] | undefined,
  spanId: number | undefined,
): Device[] => {
  if (!spanId) {
    return []
  }

  return (devices ?? []).filter(
    (device) => isSpanDevice(device) && device.settings?.span_id === spanId,
  )
}

/** The same devices, as the rows the device tables and asset headers are built from. */
export const getSpanDevicesFromRow = <T extends { device: Device }>(
  rows: T[],
  spanId: number,
): T[] =>
  rows.filter(({ device }) => isSpanDevice(device) && device.settings?.span_id === spanId)

/** The trigger from `useLazyPowerlineSpansQuery`, as far as the paging below uses it. */
type GetPowerlineSpans = (
  args: PowerlineSpansReq,
  preferCacheValue: boolean,
) => PromiseLike<{ data?: Paged<PowerlineSpansRes>; error?: unknown }>

/** Every span of a line, following the cursor page by page. */
export const fetchAllPowerlineSpans = async (
  getPowerlineSpans: GetPowerlineSpans,
  powerlineId: number,
  orgId: number,
): Promise<Span[]> => {
  let cursor: string | undefined = undefined
  let results: Span[] = []
  do {
    const { data, error } = await getPowerlineSpans(
      { id: powerlineId, params: { org_id: orgId, limit: 100, cursor } },
      true,
    )
    if (error) throw error
    if (data?.results) {
      results = results.concat(data.results)
    }
    cursor = data?.next
  } while (cursor)
  return results
}

/**
 * The span details tab, group by group. The power line link and the tower list
 * come in already built, since both link into the org's pages.
 */
export const getSpanDetailsGroups = (
  span: Span,
  locale: string,
  t: TFunction,
  { powerlineLink, towers }: { powerlineLink: ReactNode; towers: ReactNode },
): DetailsGroup[] => {
  // One height per tower, in the same order, and a null stays a dash so the
  // list keeps lining up with the towers above it.
  const towerHeights =
    span.tower_heights.length > 0
      ? span.tower_heights
          .map((height) => formatSpanValue(height, locale, formatLength))
          .join(", ")
      : undefined

  return [
    {
      titleKey: "spans.IDENTIFICATION",
      fields: [
        { labelKey: "spans.NAME", value: span.name ?? undefined },
        { labelKey: "spans.ID", value: span.id },
        { labelKey: "spans.POWERLINE", value: powerlineLink },
        { labelKey: "spans.PHASE", value: span.phase_id ?? undefined },
        { labelKey: "spans.TOWERS", value: towers },
        {
          labelKey: "spans.INSTALLED_AT",
          value: formatDayFromISO(span.installed_at),
        },
        {
          labelKey: "spans.UPDATED_AT",
          value: formatDateFromISO(span.updated_at),
        },
      ],
    },
    {
      titleKey: "spans.GEOMETRY",
      fields: [
        {
          labelKey: "spans.LENGTH",
          value: formatSpanValue(span.length, locale, formatLength),
        },
        {
          labelKey: "spans.PHASE_LENGTH",
          value: formatSpanValue(span.phase_length, locale, formatLength),
        },
        {
          labelKey: "spans.INITIAL_CONDUCTOR_LENGTH",
          value: formatSpanValue(span.initial_conductor_length, locale, formatLength),
        },
        { labelKey: "spans.TOWER_HEIGHTS", value: towerHeights },
        {
          labelKey: "spans.SAG_NOMINAL",
          value: formatSpanValue(span.sag_nominal, locale, formatLength),
        },
        {
          labelKey: "spans.SAG_MAX",
          value: formatSpanValue(span.sag_max, locale, formatLength),
        },
      ],
    },
    {
      titleKey: "spans.RATINGS",
      fields: [
        {
          labelKey: "spans.AMPACITY_NOMINAL",
          value: formatSpanValue(span.ampacity_nominal, locale, formatAmpacity),
        },
        {
          labelKey: "spans.TEMPERATURE_MAX",
          value: formatSpanValue(span.temperature_max, locale, formatTemperature),
          tooltipContent: [t("spans.TEMPERATURE_MAX_HINT")],
        },
        {
          labelKey: "spans.EMERGENCY_TEMPERATURE_MAX",
          value: formatSpanValue(
            span.emergency_temperature_max,
            locale,
            formatTemperature,
          ),
          tooltipContent: [t("spans.EMERGENCY_TEMPERATURE_MAX_HINT")],
        },
        {
          labelKey: "spans.TEMPERATURE_INSTALLATION",
          value: formatSpanValue(
            span.temperature_installation,
            locale,
            formatTemperature,
          ),
        },
        {
          labelKey: "spans.AXIAL_TENSION",
          value: formatSpanValue(span.axial_tension, locale, formatTension),
        },
        {
          labelKey: "spans.CLAMP_TYPE",
          value: span.clamp_type ? getClampTypeLabel(span.clamp_type, t) : undefined,
        },
      ],
    },
  ]
}
